import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { useCategories, useDirectory, catName } from "@/lib/hooks";
import { MetersEditor } from "@/components/Meters";
import { PhotoUpload } from "@/components/PhotoUpload";

const EMPTY = { name: "", type: "immeuble", address: "", city: "", surface: "", rooms: "", equipment: "", owner_ids: [], manager_id: "", photos: [], meters: [], description: "" };

export function Field({ label, children }) {
  return <div><Label className="text-xs text-slate-600">{label}</Label><div className="mt-1.5">{children}</div></div>;
}

export function PropertyDialog({ trigger }) {
  const { user } = useAuth();
  const { t, lang } = usePrefs();
  const cats = useCategories();
  const owners = useDirectory("proprietaire");
  const managers = useDirectory("gestionnaire", user.role !== "gestionnaire");
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try {
      await api.post("/properties", {
        ...f, surface: f.surface ? Number(f.surface) : null, rooms: f.rooms ? Number(f.rooms) : null, manager_id: f.manager_id || null,
        equipment: f.equipment.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(t("save"));
      qc.invalidateQueries({ queryKey: ["properties"] });
      setOpen(false);
      setF(EMPTY);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleOwner = (id) => setF({ ...f, owner_ids: f.owner_ids.includes(id) ? f.owner_ids.filter((x) => x !== id) : [...f.owner_ids, id] });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-sm" data-testid="property-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("newProperty")}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("name")}><Input value={f.name} onChange={set("name")} data-testid="property-name-input" /></Field>
          <Field label={t("type")}>
            <Select value={f.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.property.map((c) => <SelectItem key={c.key} value={c.key}>{catName(c, lang)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("address")}><Input value={f.address} onChange={set("address")} data-testid="property-address-input" /></Field>
          <Field label={t("city")}><Input value={f.city} onChange={set("city")} data-testid="property-city-input" /></Field>
          <Field label={t("surface")}><Input type="number" value={f.surface} onChange={set("surface")} data-testid="property-surface-input" /></Field>
          <Field label={t("rooms")}><Input type="number" value={f.rooms} onChange={set("rooms")} data-testid="property-rooms-input" /></Field>
          <div className="sm:col-span-2"><Field label={`${t("equipment")} (, )`}><Input value={f.equipment} onChange={set("equipment")} data-testid="property-equipment-input" /></Field></div>
          <Field label={t("owners")}>
            <div className="max-h-32 space-y-1.5 overflow-y-auto rounded-sm border p-2" data-testid="property-owners-list">
              {owners.map((o) => <label key={o.id} className="flex items-center gap-2 text-sm"><Checkbox checked={f.owner_ids.includes(o.id)} onCheckedChange={() => toggleOwner(o.id)} data-testid={`owner-checkbox-${o.id}`} />{o.name}</label>)}
            </div>
          </Field>
          {user.role !== "gestionnaire" && (
            <Field label={t("manager")}>
              <Select value={f.manager_id} onValueChange={set("manager_id")}>
                <SelectTrigger data-testid="property-manager-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <div className="sm:col-span-2"><Field label={t("meters")}><MetersEditor value={f.meters} onChange={set("meters")} /></Field></div>
          <div className="sm:col-span-2"><Field label={t("photos")}><PhotoUpload value={f.photos} onChange={set("photos")} accept="image/*" testId="property-photo-upload" /></Field></div>
          <div className="sm:col-span-2"><Field label={t("description")}><Textarea value={f.description} onChange={set("description")} data-testid="property-description-input" /></Field></div>
        </div>
        <Button onClick={save} disabled={!f.name} className="mt-2 rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="property-save-button">{t("create")}</Button>
      </DialogContent>
    </Dialog>
  );
}
